import React from "react";
import { Grid } from "@mui/material";
import { VehicleTextField, VehicleDateField, VehicleCheckboxField, VehicleLookupField } from "../VehicleFormFields";

interface GeneralTabProps {
  availableVehicleTypes?: Array<{ id: number; name: string }>;
  availableVehicleStatuses?: Array<{ id: number; name: string }>;
  availableDivisions?: Array<{ id: number; name: string }>;
  identityEditable?: boolean;
  hasTelematics?: boolean;
}

const GeneralTab: React.FC<GeneralTabProps> = ({
  availableVehicleTypes = [],
  availableVehicleStatuses = [],
  availableDivisions = [],
  identityEditable = true,
  hasTelematics = false,
}) => {
  return (
    <Grid container spacing={2}>
      {/* Row 1: Vehicle name, Unit number */}
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="name" 
          label="Vehicle Name" 
          tooltip={
            hasTelematics
              ? "This name will be synced back to Samsara or Motive when updated"
              : "Display name for this vehicle in your fleet"
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="unitNumber" 
          label="Unit Number" 
          placeholder="e.g., TRUCK001"
          tooltip="Unique identifier for this vehicle in your fleet"
        />
      </Grid>

      {identityEditable && (
        <>
          <Grid item xs={12} sm={6}>
            <VehicleTextField
              name="vin"
              label="VIN"
              placeholder="Vehicle Identification Number"
              tooltip="Unique vehicle identification number"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <VehicleTextField
              name="year"
              label="Year"
              placeholder="e.g., 2020"
              tooltip="Manufacturing year"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <VehicleTextField
              name="make"
              label="Make"
              placeholder="e.g., Freightliner"
              tooltip="Vehicle manufacturer"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <VehicleTextField
              name="model"
              label="Model"
              placeholder="e.g., Cascadia"
              tooltip="Vehicle model"
            />
          </Grid>
        </>
      )}
      
      {/* Start date, End date */}
      <Grid item xs={12} sm={6}>
        <VehicleDateField 
          name="startDate" 
          label="Start Date" 
          tooltip="Date when this vehicle entered service"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleDateField 
          name="endDate" 
          label="End Date" 
          tooltip="Date when this vehicle will be or was retired"
        />
      </Grid>
      
      {/* Vehicle type, Status */}
      <Grid item xs={12} sm={6}>
        <VehicleLookupField
          name="vehicleType"
          label="Vehicle Type"
          options={availableVehicleTypes}
          idFieldName="vehicleTypeId"
          nameFieldName="vehicleTypeName"
          tooltip="Select existing type or create new one by typing"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleLookupField
          name="vehicleStatus"
          label="Vehicle Status"
          options={availableVehicleStatuses}
          idFieldName="vehicleStatusId"
          nameFieldName="vehicleStatusName"
          tooltip="Current operational status of the vehicle"
        />
      </Grid>
      
      {/* Division, Is active */}
      <Grid item xs={12} sm={6}>
        <VehicleLookupField
          name="division"
          label="Division"
          options={availableDivisions}
          idFieldName="divisionId"
          nameFieldName="divisionName"
          tooltip="Business division or department this vehicle belongs to"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleCheckboxField 
          name="isActive" 
          label="Active" 
          tooltip="Whether this vehicle is currently active in your fleet"
        />
      </Grid>
    </Grid>
  );
};

export default GeneralTab;
